import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound, Loader2, Sparkles, PlugZap, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

function Card({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl bg-card border border-border p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        <h2 className="font-serif text-xl text-foreground">{title}</h2>
      </div>
      {children}
    </section>
  );
}

const inputCls =
  "w-full px-3.5 py-2.5 rounded-md border border-input bg-white/70 focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200";
const btnCls =
  "flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors duration-200";

function TwoFactorSection() {
  const { user, reload } = useAuth();
  const [setup, setSetup] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null);
      setCode("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Two-factor authentication disabled");
      setCode("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (user?.totp_enabled) {
    return (
      <Card title="Two-factor authentication" icon={ShieldCheck}>
        <p className="text-sm text-pillar-health font-medium mb-4" data-testid="twofa-status">
          Enabled — codes required at sign in.
        </p>
        <div className="flex items-end gap-2 max-w-sm">
          <div className="flex-1">
            <label className="block text-sm text-foreground/80 mb-1.5">Enter a code to disable</label>
            <input
              className={inputCls}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="twofa-disable-code"
            />
          </div>
          <button onClick={disable} disabled={busy || code.length !== 6} className={btnCls} data-testid="twofa-disable-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />} Disable
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Two-factor authentication" icon={ShieldOff}>
      <p className="text-sm text-muted-foreground mb-4" data-testid="twofa-status">
        Disabled. Add an authenticator app for an extra layer of security (optional).
      </p>
      {!setup ? (
        <button onClick={startSetup} disabled={busy} className={btnCls} data-testid="twofa-setup-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Enable 2FA
        </button>
      ) : (
        <div className="max-w-sm">
          <p className="text-sm text-foreground/80 mb-3">
            Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code.
          </p>
          <img src={setup.qr} alt="2FA QR code" className="w-44 h-44 rounded-md border border-border mb-2" data-testid="twofa-qr" />
          <p className="text-xs text-muted-foreground mb-3 break-all">
            Or enter this key manually: <code className="text-foreground">{setup.secret}</code>
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-sm text-foreground/80 mb-1.5">Verification code</label>
              <input
                className={inputCls}
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="twofa-enable-code"
              />
            </div>
            <button onClick={enable} disabled={busy || code.length !== 6} className={btnCls} data-testid="twofa-enable-btn">
              Confirm
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Change password" icon={KeyRound}>
      <form onSubmit={submit} className="max-w-sm space-y-4" data-testid="change-password-form">
        <div>
          <label className="block text-sm text-foreground/80 mb-1.5">Current password</label>
          <input type="password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password" />
        </div>
        <div>
          <label className="block text-sm text-foreground/80 mb-1.5">New password</label>
          <input type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} placeholder="At least 8 characters" data-testid="new-password" />
        </div>
        <button type="submit" disabled={busy} className={btnCls} data-testid="change-password-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
        </button>
      </form>
    </Card>
  );
}

const PROVIDERS = [
  { key: "openai", label: "ChatGPT (OpenAI)" },
  { key: "claude", label: "Claude (Anthropic)" },
  { key: "kimi_local", label: "Kimi3 (Local)" },
];

function AISettingsSection() {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState("openai");
  const [openaiKey, setOpenaiKey] = useState("");
  const [openaiModel, setOpenaiModel] = useState("");
  const [claudeKey, setClaudeKey] = useState("");
  const [claudeModel, setClaudeModel] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/settings");
      setData(data);
      setProvider(data.provider || "openai");
      setOpenaiModel(data.openai.model || "");
      setClaudeModel(data.claude.model || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buildPayload = () => {
    const p = { provider };
    if (openaiModel) p.openai_model = openaiModel;
    if (openaiKey) p.openai_api_key = openaiKey;
    if (claudeModel) p.claude_model = claudeModel;
    if (claudeKey) p.claude_api_key = claudeKey;
    return p;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ai/settings", buildPayload());
      setData(data);
      setOpenaiKey("");
      setClaudeKey("");
      toast.success("AI settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveAndTest = async () => {
    setTesting(true);
    try {
      await api.put("/ai/settings", buildPayload());
      setOpenaiKey("");
      setClaudeKey("");
      const { data: res } = await api.post("/ai/test");
      if (res.success) toast.success(res.message);
      else toast.error(res.message || "The AI did not reply as expected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  if (!data) {
    return (
      <Card title="AI assistant" icon={Sparkles}>
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      </Card>
    );
  }

  const dm = data.default_models;

  return (
    <Card title="AI assistant" icon={Sparkles}>
      <p className="text-sm text-muted-foreground mb-4" data-testid="ai-settings-intro">
        Powers the "Analyse this week's progress" button. Choose your provider and add your own key —
        keys are encrypted and never leave the server. For a local Kimi3, just point to its reachable URL.
      </p>

      <div className="mb-5">
        <label className="block text-sm text-foreground/80 mb-1.5">Provider</label>
        <div className="flex flex-wrap gap-2" data-testid="ai-provider-group">
          {PROVIDERS.map((p) => (
            <button
              key={p.key}
              onClick={() => setProvider(p.key)}
              data-testid={`ai-provider-${p.key}`}
              className={`px-3.5 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                provider === p.key
                  ? "bg-foreground text-background border-foreground"
                  : "bg-white/70 text-foreground/80 border-input hover:border-foreground/40"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {provider === "openai" && (
        <div className="space-y-4 max-w-md" data-testid="ai-fields-openai">
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5 flex items-center gap-2">
              OpenAI API key
              {data.openai.configured && <span className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> saved</span>}
            </label>
            <input type="password" className={inputCls} value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)}
              placeholder={data.openai.configured ? "•••••••• (leave blank to keep)" : "sk-..."} data-testid="ai-openai-key" />
          </div>
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5">Model</label>
            <input className={inputCls} value={openaiModel} onChange={(e) => setOpenaiModel(e.target.value)} placeholder={dm.openai} data-testid="ai-openai-model" />
          </div>
        </div>
      )}

      {provider === "claude" && (
        <div className="space-y-4 max-w-md" data-testid="ai-fields-claude">
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5 flex items-center gap-2">
              Anthropic API key
              {data.claude.configured && <span className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> saved</span>}
            </label>
            <input type="password" className={inputCls} value={claudeKey} onChange={(e) => setClaudeKey(e.target.value)}
              placeholder={data.claude.configured ? "•••••••• (leave blank to keep)" : "sk-ant-..."} data-testid="ai-claude-key" />
          </div>
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5">Model</label>
            <input className={inputCls} value={claudeModel} onChange={(e) => setClaudeModel(e.target.value)} placeholder={dm.claude} data-testid="ai-claude-model" />
          </div>
        </div>
      )}

      {provider === "kimi_local" && (
        <div className="max-w-md rounded-md border border-border bg-secondary/40 p-4" data-testid="ai-fields-kimi">
          {data.kimi_local.configured ? (
            <p className="text-sm text-foreground/80" data-testid="ai-kimi-status">
              The local Kimi3 server is set up by your admin
              {data.kimi_local.model ? ` (model: ${data.kimi_local.model})` : ""}. Just pick this
              provider and you're ready to go.
            </p>
          ) : (
            <p className="text-sm text-foreground/80" data-testid="ai-kimi-status">
              Kimi3 (Local) isn't configured yet. An admin needs to set the server URL under
              <span className="font-medium"> Admin → AI</span>.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-6">
        <button onClick={save} disabled={saving} className={btnCls} data-testid="ai-save-btn">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
        <button onClick={saveAndTest} disabled={testing}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-input bg-white/70 font-medium hover:border-foreground/40 disabled:opacity-50 transition-colors duration-200"
          data-testid="ai-test-btn">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Save & test connection
        </button>
      </div>
    </Card>
  );
}

export default function AppSettings() {
  return (
    <div className="max-w-3xl px-6 md:px-12 py-10 md:py-14" data-testid="app-settings">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Account</p>
      <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-8">Settings</h1>
      <AISettingsSection />
      <TwoFactorSection />
      <PasswordSection />
    </div>
  );
}
